import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { Category } from '../types';

interface CategoryManagerProps {
  isOpen?: boolean;
  onClose?: () => void;
  onCategoryAdded?: () => void;
}

const CategoryManager: React.FC<CategoryManagerProps> = ({ isOpen = true, onClose, onCategoryAdded }) => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    displayName: ''
  });

  useEffect(() => {
    if (isOpen) {
      fetchCategories();
    }
  }, [isOpen]);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await api.get('/categories');
      if (response.data && response.data.data) {
        // Nur Custom-Kategorien filtern
        const customCategories = response.data.data.categories.filter((cat: any) => cat.isCustom);
        setCategories(customCategories);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Kategorien:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      if (!formData.name || !formData.displayName) {
        alert('Bitte geben Sie einen Kategorie-Namen ein');
        return;
      }
      
      await api.post('/categories', {
        name: formData.name,
        displayName: formData.displayName
      });

      setFormData({ name: '', displayName: '' });
      fetchCategories();
      onCategoryAdded?.();
    } catch (error: any) {
      console.error('Fehler beim Erstellen der Kategorie:', error);
      alert(error.response?.data?.message || 'Fehler beim Erstellen der Kategorie');
    }
  };

  const handleDelete = async (categoryName: string) => {
    if (window.confirm('Kategorie wirklich löschen?')) {
      try {
        await api.delete(`/categories/${categoryName}`);
        fetchCategories();
        onCategoryAdded?.();
      } catch (error: any) {
        console.error('Fehler beim Löschen der Kategorie:', error);
        alert(error.response?.data?.message || 'Fehler beim Löschen der Kategorie');
      }
    }
  };

  if (!isOpen) return null;

  const isModal = onClose !== undefined;

  const content = (
    <div style={{ padding: '20px' }}>
      <h2>Eigene Kategorien</h2>
      <p style={{ color: '#64748b', marginBottom: '20px' }}>
        Erstelle eigene Kategorien für deine Transaktionen.
      </p>

      {/* Neue Kategorie hinzufügen */}
      <div style={{ 
        marginBottom: '30px', 
        padding: '20px', 
        backgroundColor: '#f8fafc',
        borderRadius: '8px',
        border: '1px solid #e2e8f0'
      }}>
        <h3 style={{ marginTop: 0 }}>Neue Kategorie</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '10px', alignItems: 'end' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>
              Kategorie-Name
            </label>
            <input
              type="text"
              value={formData.displayName}
              onChange={(e) => {
                const displayName = e.target.value;
                const name = displayName
                  .toUpperCase()
                  .replace(/Ä/g, 'AE')
                  .replace(/Ö/g, 'OE')
                  .replace(/Ü/g, 'UE')
                  .replace(/ß/g, 'SS')
                  .replace(/[^A-Z0-9]/g, '_');
                setFormData({ name, displayName });
              }}
              placeholder="z.B. Haustiere"
              style={{ width: '100%', padding: '8px' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>
              Technischer Name
            </label>
            <input
              type="text"
              value={formData.name}
              disabled
              style={{ width: '100%', padding: '8px', backgroundColor: '#f1f5f9', color: '#64748b' }}
            />
          </div>
          <button
            onClick={handleSubmit}
            style={{
              padding: '8px 16px',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Hinzufügen
          </button>
        </div>
      </div>

      {/* Liste der Kategorien */}
      {loading ? (
        <p>Lade Kategorien...</p>
      ) : categories.length === 0 ? (
        <p style={{ color: '#94a3b8', textAlign: 'center', padding: '40px' }}>
          Noch keine eigenen Kategorien vorhanden.<br/>
          Füge deine erste Kategorie hinzu!
        </p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#f1f5f9' }}>
              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>Name</th>
              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>Technischer Name</th>
              <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #e2e8f0' }}>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {categories.map((category) => (
              <tr key={category.name} style={{ borderBottom: '1px solid #e2e8f0' }}>
                <td style={{ padding: '12px', fontWeight: 500 }}>{category.displayName}</td>
                <td style={{ padding: '12px', color: '#64748b', fontFamily: 'monospace' }}>{category.name}</td>
                <td style={{ padding: '12px', textAlign: 'right' }}>
                  <button
                    onClick={() => handleDelete(category.name)}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Löschen
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );

  if (isModal) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px'
      }}>
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '30px',
          maxWidth: '800px',
          width: '100%',
          maxHeight: '80vh',
          overflow: 'auto',
          boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
          position: 'relative'
        }}>
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              top: '15px',
              right: '15px',
              background: 'transparent',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#64748b'
            }}
          >
            ✕
          </button>
          {content}
        </div>
      </div>
    );
  }

  return content;
};

export default CategoryManager;